import { Box, Grid, Stack, Typography, useTheme } from "@mui/material";
import { AppImages } from "app/appImages";
import { MprcButton, MprcCard, MprcRequestErrorReport, MprcRequestIndicator } from "components";
import { ChallengeLmsSettingsResponse, ChallengeMode, ChallengePendingTopicResult, ChallengeUserTopicResponse } from "generated/CoreApi_v1";
import { usePageTitle } from "hooks/usePageTitle";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { validate as isValidUUID } from 'uuid';
import { AssPlayerChallenge } from "views/assessment/player/AssPlayerChallenge";

/////////////////////////////////////////////////////////////////////////////////

export const NewKnowledgeTest: React.FC = () => {

    const api = useCoreApi();
    const { t } = useTranslation("assessment");
    const session = useSession();
    const theme = useTheme();

    const [searchParams, setSearchParams] = useSearchParams();

    const [starting, setStarting] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [settings, setSettings] = useState<ChallengeLmsSettingsResponse>();
    const [seeAll, setSeeAll] = useState<boolean>(false);
    const [pending, setPending] = useState<ChallengePendingTopicResult>();
    const [challenge, setChallenge] = useState<ChallengeUserTopicResponse>();
    const [viewError, setViewError] = useState<any>();
    const [dialog, setDialog] = useState<any>();

    usePageTitle(t("Challenge.PageTitle"));

    ///
    const handleSelectTopic = (topicId: string, isRetention: boolean, instanceId?: string) => {
        setDialog(
            <AssPlayerChallenge
                topicId={topicId}
                challengeMode={isRetention ? ChallengeMode.Retention : ChallengeMode.Default}
                instanceId={instanceId}
                onClose={() => {
                    setDialog(null);
                    setStarting(true);
                }}
            />
        );
    };

    ///
    async function loadTopics() {
        setLoading(true);
        const firstTopicId = searchParams.get("t");
        try {
            const result = await api.v1.assPlayer.challengeUserTopics({
                top3: !seeAll,
                firstTopicId: !!firstTopicId && isValidUUID(firstTopicId) ? firstTopicId : null
            });
            setChallenge(result.data);
            setPending(undefined);
        }
        catch (e) { setViewError(e); }
        finally {
            setLoading(false);
            setSearchParams("");
        }
    }

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        async function getData() {
            try {
                var lmsSetting = await api.v1.assPlayer.getChallengeSettings({});
                var resultPending = await api.v1.assPlayer.challengeAnswerPending({});

                setSettings(lmsSetting.data);
                if (resultPending.data && resultPending.data.result) {
                    setPending(resultPending.data.result);
                } else {
                    loadTopics();
                }
            } catch (e) {
                setViewError(e);
            } finally {
                setStarting(false);
            }
        }

        if (starting) getData();

    }, [starting]);

    useEffect(() => {
        if (seeAll) loadTopics();
    }, [seeAll]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    const RenderContent = () => {
        return (
            <Stack direction="column" spacing={2} marginTop={4}>
                <Typography variant="body1">{t("Challenge.SelectTopicText")}</Typography>
                {
                    challenge?.topics?.map((item, index) => {
                        return (
                            <Box
                                key={index}
                                sx={(theme) => ({
                                    borderRadius: "22px",
                                    borderColor: theme.palette.highlight.main,
                                    borderStyle: "solid",
                                    borderWidth: "1px",
                                    cursor: "pointer",
                                })}
                                onClick={() => handleSelectTopic(item.topicId, item.isRetention)}
                            >
                                <Stack direction="column" margin="16px">
                                    <Typography variant="h3" color={theme.palette.highlight.main}>{item.name}</Typography>
                                    <Typography variant="caption">
                                        {
                                            item.isRetention
                                                ? t("Challenge.RetentionText")
                                                : item.grantSealProvenKnowledge
                                                    ? t("Challenge.GrantSealText", { count: item.pendingQuestions })
                                                    : t("Challenge.PendingQuestionsText", { count: item.pendingQuestions })
                                        }
                                    </Typography>
                                </Stack>
                            </Box>
                        );
                    })
                }

                {
                    challenge != undefined && challenge.totalTopics > 3 && !seeAll &&
                    <MprcButton
                        text={t("Challenge.SeeAllButtonText")}
                        variant="text"
                        onClick={() => setSeeAll(true)}
                    />
                }
            </Stack>
        );
    };

    ///
    const RenderPending = () => {

        if (pending == null) return null;

        return (
            <Stack direction="column" spacing={1} padding={2}>
                <Typography variant="h1">{t("Challenge.Pending.Title")}</Typography>
                <Grid container spacing={2}>
                    <Grid>
                        {
                            pending.topicThumbnailUrl &&
                            <Box sx={{ width: 220, height: 124, borderRadius: 'sm', p: 1 }}>
                                <img src={pending.topicThumbnailUrl} style={{ objectFit: "contain" }} />
                            </Box>
                        }
                    </Grid>
                    <Grid size="grow">
                        <Grid container direction="column" height="100%">
                            <Grid size="grow">
                                <Typography
                                    variant="h6"
                                    fontSize="18px"
                                    fontWeight="700"
                                    color={theme.palette.text.primary}
                                >{pending.topicName}</Typography>
                            </Grid>
                            <Grid>
                                <Typography variant="body2" component="p" textTransform="uppercase">
                                    <strong>{t("Challenge.Pending.WhatYouWantToDoText")}</strong>
                                </Typography>
                                <Stack direction="row" spacing={2} mt={1}>
                                    <MprcButton
                                        text={t("Challenge.Pending.FinishThisTestButtonText")}
                                        title={t("Challenge.Pending.FinishThisTestButtonTitle")}
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleSelectTopic(pending.topicId, pending.challengeMode == ChallengeMode.Retention, pending.instanceId)}
                                    />
                                    <MprcButton
                                        text={t("Challenge.Pending.SelectTopicButtonText")}
                                        title={t("Challenge.Pending.SelectTopicButtonTitle")}
                                        variant="contained"
                                        color="primary"
                                        onClick={() => loadTopics()}
                                    />
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Stack>
        );
    };

    ///
    const RenderNoChallenge = () => {
        return (
            <Stack direction="column" alignItems="center" spacing={4} padding={4}>
                <Stack direction="column" alignItems="center">
                    <Typography variant="h1">{t("Challenge.Pending.NoChallengeAnswerTitleL1")}</Typography>
                    <Typography variant="h1">{t("Challenge.Pending.NoChallengeAnswerTitleL2")}</Typography>
                </Stack>
                <Box
                    component="img"
                    height="257px"
                    width="257px"
                    src={settings?.thumbnail?.url ?? AppImages.AssessmentTopicChallengeThumbnail}
                />
            </Stack>
        );
    };

    ///
    const RenderTopics = () => {
        return (
            <>
                {
                    settings && challenge &&
                    <Stack direction="column" margin={2}>
                        <Typography variant="h1">{t("Challenge.Title")}</Typography>
                        <Grid container spacing={2}>
                            <Grid size="grow">
                                {
                                    settings.headlineR.find(x => x.locale == session.uiCulture) &&
                                    <Typography variant="body1" color={theme.palette.warning.main}>
                                        {settings.headlineR.find(x => x.locale == session.uiCulture)?.text}
                                    </Typography>
                                }
                                {
                                    challenge.topics.length == 0
                                        ?
                                        <Typography variant="bodyNotice">{t("Challenge.EmptyTestsText")}</Typography>
                                        :
                                        RenderContent()
                                }
                            </Grid>
                            {
                                challenge.topics.length > 0 &&
                                <Grid>
                                    <Box
                                        component="img"
                                        height="257px"
                                        width="257px"
                                        src={settings.thumbnail?.url ?? AppImages.AssessmentTopicChallengeThumbnail}
                                    />
                                </Grid>
                            }
                        </Grid>
                        <Box paddingY={1} paddingX={2} mt={2}
                            sx={
                                (theme) => ({
                                    backgroundColor: theme.palette.notice.main,
                                    borderRadius: "6px"
                                })
                            }>
                            <Grid container >
                                <Grid size="grow">
                                    <Typography variant="subtitle1" textTransform="uppercase">{t("Challenge.EstimatedTimeText")}: <strong>
                                        {`${challenge.estimatedTime} ${t(challenge.estimatedTime > 1 ? "Literal.Minutes" : "Literal.Minute", { ns: "shared" })}`}
                                    </strong></Typography>
                                </Grid>
                                <Grid>
                                    <Typography variant="subtitle1" textTransform="uppercase">{t("Challenge.EarnUpToText")} <strong>{t("Challenge.PointsText", { count: challenge.points })}</strong></Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Stack>
                }
            </>
        );
    };

    return (
        <>
            <MprcRequestErrorReport error={viewError} />

            <MprcRequestIndicator type="request" visible={loading || starting} />

            {dialog}

            <Grid container justifyContent="center" alignItems="center" direction="column" width="100%" spacing={2}>
                <Grid size="grow">
                    <Grid container justifyContent="center" alignItems="center">
                        <MprcCard sx={(theme) => ({ width: theme.breakpoints.values.sm })}>
                            {
                                pending
                                    ? RenderPending()
                                    : challenge?.totalTopics == 0
                                        ? RenderNoChallenge()
                                        : RenderTopics()
                            }
                        </MprcCard>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};
